/**
 * Subscription API Communication Layer
 * Handles all subscription-related HTTP requests to the backend API
 */

import { fetchWithTimeout, API_URL, DEFAULT_TIMEOUT, parseApiEnvelope } from './api.js';

/**
 * Subscription API client
 * Provides methods for subscription status, checkout, and portal management
 */
export class SubscriptionAPI {
  /**
   * Handle standardized API responses
   * Unwraps data from { success: true, data: {...} } format
   * @private
   */
  static async handleResponse(response) {
    try {
      const result = await response.json();

      if (!response.ok) {
        // Return error response with error code for handling; `metadata` is
        // carried through for callers that discriminate on it.
        return {
          success: false,
          error: result.error || 'Request failed',
          code: result.code,
          metadata: result.metadata || null,
          status: response.status
        };
      }

      // Wrap the unwrapped data back in { success: true, data: ... } for callers
      // (SubscriptionAPI callers expect this wrapper, unlike API.handleResponse)
      return { success: true, data: parseApiEnvelope(result) };
    } catch (error) {
      console.error('Error parsing subscription response:', error);
      return { success: false, error: 'Failed to parse response' };
    }
  }

  /**
   * Get current user's subscription status (payload v4).
   *
   * The payload depends on the caller's own rows only: their own entitling
   * subscription, or an active beta grant, or nothing (Basic). The partner
   * rate is a discount on the caller's own subscription, reported in its own
   * block — there is no cross-partner entitlement.
   *
   * @returns {Promise<Object>} Subscription status
   * @property {boolean} isSubscribed - Whether user has premium access
   * @property {'basic'|'premium'} plan - Winning entitlement tier
   * @property {boolean} hasOwnPaidSubscription - Whether the user's own subscription
   *   row is the entitling source (false for beta-grant access)
   * @property {string} status - Status: FREE, TRIALING, ACTIVE, PAST_DUE, CANCELED, UNPAID
   * @property {string|null} currentPeriodEnd - ISO date when billing period ends
   * @property {string|null} gracePeriodEndsAt - ISO date when grace period ends
   * @property {boolean} cancelAtPeriodEnd - Whether subscription will cancel at period end
   * @property {string|null} priceId - Stripe price ID for current plan (null if no subscription)
   * @property {number|null} amount - List price per billing period, in dollars (20);
   *   the partner rate is NOT folded in here — read `partnerRate.amount`
   * @property {string|null} currency - Currency code (e.g. 'usd')
   * @property {'month'|null} interval - Billing interval (monthly only)
   * @property {number|null} intervalCount - Number of intervals between billings (1)
   * @property {{applied: boolean, amount: number|null}} partnerRate - Whether the
   *   partner-rate coupon is applied to the caller's own subscription, and the
   *   discounted monthly amount (null when the rate isn't configured)
   * @property {boolean} trialAvailable - Whether a future checkout for this user
   *   would carry the free trial (trial configured server-side AND never
   *   consumed) — the only signal that may render trial copy
   * @property {Object} [retentionStats] - Lapsed-user accrual counts (UNPAID/CANCELED only)
   */
  static async getStatus() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/subscription/status`, {
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getSubscriptionStatus:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get subscription configuration (prices, availability)
   * @returns {Promise<Object>} Subscription config
   */
  static async getConfig() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/subscription/config`, {
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getSubscriptionConfig:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create a Stripe Checkout session for subscription purchase
   * @param {Object} options - Checkout options
   * @param {string} [options.priceId] - Stripe price ID (the monthly Premium price)
   * @param {string} [options.successUrl] - Redirect URL on success
   * @param {string} [options.cancelUrl] - Redirect URL on cancel
   * @returns {Promise<Object>} Checkout session with sessionId and url
   */
  static async createCheckoutSession(options = {}) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/subscription/create-checkout-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(options)
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in createCheckoutSession:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Create a Stripe Customer Portal session for subscription management.
   * Optionally deep-links into a specific portal flow.
   *
   * @param {Object} options - Portal options
   * @param {string} [options.returnUrl] - URL to return to after portal
   * @param {'default'|'subscription_cancel'|'payment_method_update'} [options.flowType]
   *   Deep-link target. Omit for the default portal home.
   * @returns {Promise<Object>} Portal session with url
   */
  static async createPortalSession(options = {}) {
    try {
      const response = await fetchWithTimeout(`${API_URL}/subscription/create-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          returnUrl: options.returnUrl,
          flowType: options.flowType
        })
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in createPortalSession:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get current user's daily-message-quota state.
   * @returns {Promise<Object>} Usage snapshot
   * @property {number|null} limit - Daily cap (null when unlimited)
   * @property {number|null} used - Messages sent today (null when unlimited)
   * @property {number|null} remaining - Remaining today (null when unlimited)
   * @property {string|null} resetAt - Next UTC midnight ISO string (null when unlimited)
   * @property {boolean} unlimited - True for Premium/Guest/exempt paths
   */
  static async getUsage() {
    try {
      const response = await fetchWithTimeout(`${API_URL}/subscription/usage`, {
        credentials: 'include'
      });
      return await this.handleResponse(response);
    } catch (error) {
      console.error('Network error in getUsage:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Poll subscription status until active (for post-checkout)
   * Uses exponential backoff: 1s, 2s, 4s, 8s, 16s
   * @param {number} [maxAttempts=5] - Maximum polling attempts
   * @returns {Promise<Object>} Final subscription status
   * @throws {Error} If subscription doesn't become active within attempts
   */
  static async waitForSubscriptionActive(maxAttempts = 5) {
    for (let i = 0; i < maxAttempts; i++) {
      const result = await this.getStatus();

      if (result.success && result.data?.isSubscribed) {
        return result;
      }

      // Exponential backoff: 1s, 2s, 4s, 8s, 16s
      const delay = 1000 * Math.pow(2, i);
      await new Promise(resolve => setTimeout(resolve, delay));
    }

    throw new Error('Subscription activation timeout');
  }

  /**
   * Redirect to Stripe Checkout
   * Creates a checkout session and redirects the user
   * @param {Object} options - Checkout options
   * @param {string} [options.priceId] - Stripe price ID
   * @returns {Promise<void>}
   * @throws {Error} If checkout session creation fails
   */
  static async redirectToCheckout(options = {}) {
    const result = await this.createCheckoutSession(options);

    if (!result.success) {
      throw new Error(result.error || 'Failed to create checkout session');
    }

    if (result.data?.url) {
      window.location.href = result.data.url;
    } else {
      throw new Error('No checkout URL returned');
    }
  }

  /**
   * Redirect to Stripe Customer Portal
   * Creates a portal session and redirects the user
   * @param {Object} options - Portal options
   * @param {string} [options.returnUrl] - URL to return to after portal
   * @param {'default'|'subscription_cancel'|'payment_method_update'} [options.flowType]
   *   Deep-link target passed through to createPortalSession.
   * @returns {Promise<void>}
   * @throws {Error} If portal session creation fails
   */
  static async redirectToPortal(options = {}) {
    const result = await this.createPortalSession(options);

    if (!result.success) {
      throw new Error(result.error || 'Failed to create portal session');
    }

    if (result.data?.url) {
      window.location.href = result.data.url;
    } else {
      throw new Error('No portal URL returned');
    }
  }
}
